<template>
  <div class="relatorio-status-parceiro">
    <div class="container-fluid py-4">
      <!-- Linha superior: Voltar (esq) | Título (centro) | Gerar PDF ou Logo (dir) -->
      <div
        class="row-top d-flex justify-content-between align-items-center mb-4"
      >
        <div class="col-top col-top-left">
          <button
            v-if="mostrarVoltar"
            class="btn btn-light no-print"
            @click="$router.push({ name: 'parceiros' })"
          >
            <i class="fa fa-arrow-left"></i> Voltar
          </button>
        </div>
        <h1 class="titulo-relatorio">Relatório de Status por Mês</h1>
        <div class="col-top col-top-right">
          <button
            v-if="mostrarGerarPdf"
            class="btn btn-success btn-fill no-print"
            @click="gerarPdf"
          >
            Gerar PDF
          </button>
        </div>
      </div>

      <!-- Cabeçalho: 4 informações à esquerda, logo à direita -->
      <div class="relatorio-card mb-4">
        <div
          class="card-body d-flex align-items-center justify-content-between flex-wrap"
        >
          <div class="info-parceiro text-left">
            <p class="mb-1">
              <strong>Parceiro:</strong> {{ nomeParceiro || '—' }}
            </p>
            <p class="mb-1">
              <strong>Código:</strong> {{ codigoParceiro || '—' }}
            </p>
            <p class="mb-1">
              <strong>Data de emissão:</strong> {{ dataEmissao }}
            </p>
            <p class="mb-1">
              <strong>Ano do relatório:</strong>
              <span class="ml-2 no-print">
                <select
                  v-model="anoSelecionado"
                  class="form-control d-inline-block w-auto"
                  @change="carregarRelatorio"
                >
                  <option v-for="y in anosDisponiveis" :key="y" :value="y">{{
                    y
                  }}</option>
                </select>
              </span>
              <span class="print-only espaco-ano">{{ anoSelecionado }}</span>
            </p>
          </div>
          <div class="logo-joule print-only">
            <img src="/img/joule-logo.png" alt="Joule" class="img-logo" />
          </div>
        </div>
      </div>

      <div v-if="loading" class="text-center py-5">Carregando...</div>
      <div v-else-if="erro" class="alert alert-danger">{{ erro }}</div>
      <template v-else-if="report">
        <!-- Tabela principal: linhas = meses, colunas = status -->
        <div class="relatorio-card mb-4">
          <div class="card-header text-center">
            <h5 class="mb-0">Status por mês</h5>
          </div>
          <div class="card-body overflow-auto">
            <table class="table table-bordered table-sm tabela-status">
              <thead>
                <tr>
                  <th>Mês</th>
                  <th
                    v-for="s in statusOrdenados"
                    :key="s.key"
                    :class="{
                      'col-aguardando-inspecao': s.key === 'aguardando_inspecao'
                    }"
                  >
                    {{ s.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="mes in 12" :key="mes">
                  <td>
                    <strong>{{ nomeMes(mes) }}</strong>
                  </td>
                  <td
                    v-for="s in statusOrdenados"
                    :key="s.key"
                    :class="{
                      'col-aguardando-inspecao': s.key === 'aguardando_inspecao'
                    }"
                  >
                    {{
                      formatCelulaTabela(
                        report.tabela[s.key] && report.tabela[s.key][mes]
                      )
                    }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Tabela resumo: média, maior mês, menor mês, soma -->
        <div class="relatorio-card mb-4">
          <div class="card-header text-center">
            <h5 class="mb-0">Resumo por status</h5>
          </div>
          <div class="card-body overflow-auto">
            <table class="table table-bordered table-sm tabela-resumo">
              <thead>
                <tr>
                  <th></th>
                  <th
                    v-for="s in statusOrdenados"
                    :key="s.key"
                    :class="{
                      'col-aguardando-inspecao': s.key === 'aguardando_inspecao'
                    }"
                  >
                    {{ s.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Média Mensal</strong></td>
                  <td
                    v-for="s in statusOrdenados"
                    :key="s.key"
                    :class="{
                      'col-aguardando-inspecao': s.key === 'aguardando_inspecao'
                    }"
                  >
                    {{ formatNum(report.mediaMensal[s.key]) }}
                  </td>
                </tr>
                <tr>
                  <td><strong>Maior Mês</strong></td>
                  <td
                    v-for="s in statusOrdenados"
                    :key="s.key"
                    :class="{
                      'col-aguardando-inspecao': s.key === 'aguardando_inspecao'
                    }"
                  >
                    {{ nomeMes(report.maiorMes[s.key]) }}
                  </td>
                </tr>
                <tr>
                  <td><strong>Menor Mês</strong></td>
                  <td
                    v-for="s in statusOrdenados"
                    :key="s.key"
                    :class="{
                      'col-aguardando-inspecao': s.key === 'aguardando_inspecao'
                    }"
                  >
                    {{ nomeMes(report.menorMes[s.key]) }}
                  </td>
                </tr>
                <tr>
                  <td><strong>Soma</strong></td>
                  <td
                    v-for="s in statusOrdenados"
                    :key="s.key"
                    :class="{
                      'col-aguardando-inspecao': s.key === 'aguardando_inspecao'
                    }"
                  >
                    {{ report.soma[s.key] }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Sazonalidade -->
        <div class="relatorio-card mb-4">
          <div class="card-header text-center">
            <h5 class="mb-0">Sazonalidade</h5>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-3 col-6 mb-2">
                <strong>Verão:</strong>
                {{ formatNum(report.sazonalidade.verao)
                }}{{ report.sazonalidade.verao != null ? '%' : '' }}
              </div>
              <div class="col-md-3 col-6 mb-2">
                <strong>Outono:</strong>
                {{ formatNum(report.sazonalidade.outono)
                }}{{ report.sazonalidade.outono != null ? '%' : '' }}
              </div>
              <div class="col-md-3 col-6 mb-2">
                <strong>Inverno:</strong>
                {{ formatNum(report.sazonalidade.inverno)
                }}{{ report.sazonalidade.inverno != null ? '%' : '' }}
              </div>
              <div class="col-md-3 col-6 mb-2">
                <strong>Primavera:</strong>
                {{ formatNum(report.sazonalidade.primavera)
                }}{{ report.sazonalidade.primavera != null ? '%' : '' }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import format from 'date-fns/format';
import parceiroProjectService from 'src/services/parceiroProject';

const STATUS_LABELS = {
  recebido: 'Recebido',
  elaboracao: 'Elaboração',
  em_analise: 'Em análise',
  reanalise: 'Reanálise',
  reprovado_por_debito: 'Reprovado débito',
  aprovado: 'Aprovado',
  aguardando_inspecao: 'Aguardando Insp.',
  obra: 'Obra',
  finalizado: 'Finalizado'
};

const STATUS_ORDER = [
  'recebido',
  'elaboracao',
  'em_analise',
  'reanalise',
  'reprovado_por_debito',
  'aprovado',
  'aguardando_inspecao',
  'obra',
  'finalizado'
];

const MESES = [
  '',
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
];

export default {
  name: 'RelatorioStatusParceiro',
  props: {
    codigoParceiro: { type: String, required: true }
  },
  data() {
    const anoAtual = new Date().getFullYear();
    const anoMinimo = 2026;
    const anos = [];
    for (let y = anoMinimo; y <= anoAtual; y += 1) anos.push(y);
    return {
      nomeParceiro: '',
      report: null,
      loading: false,
      erro: null,
      anoSelecionado: anos.length ? anos[anos.length - 1] : anoMinimo,
      anosDisponiveis: anos.length ? anos : [anoMinimo],
      mostrarGerarPdf: true,
      mostrarVoltar: true,
      dataEmissao: format(new Date(), 'dd/MM/yyyy HH:mm')
    };
  },
  computed: {
    statusOrdenados() {
      return STATUS_ORDER.map(key => ({
        key,
        label: STATUS_LABELS[key] || key
      }));
    }
  },
  mounted() {
    this.nomeParceiro = this.$route.query.nomeParceiro || '';
    this.carregarRelatorio();
  },
  methods: {
    nomeMes(mes) {
      return MESES[mes] || mes;
    },
    formatNum(n) {
      if (n == null) return '—';
      return Number(n) === n ? n.toFixed(2) : n;
    },
    formatCelulaTabela(val) {
      if (val == null) return '—';
      return val;
    },
    carregarRelatorio() {
      this.loading = true;
      this.erro = null;
      parceiroProjectService
        .getStatusReport({
          codigoParceiro: this.codigoParceiro,
          ano: this.anoSelecionado
        })
        .then(response => {
          this.report = response.data;
        })
        .catch(error => {
          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            this.erro = error.response.data.message;
          } else {
            this.erro = 'Erro ao carregar relatório.';
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    gerarPdf() {
      const tituloOriginal = document.title;
      const nome = (this.nomeParceiro || '—').replace(/[/\\:*?"<>|]/g, '-');
      const codigo = (this.codigoParceiro || '—').replace(/[/\\:*?"<>|]/g, '-');
      document.title = `Relatório de Status por mês de ${this.anoSelecionado} - ${codigo} - ${nome}`;

      this.mostrarGerarPdf = false;
      this.mostrarVoltar = false;
      this.$nextTick(() => {
        setTimeout(() => {
          window.print();
          document.title = tituloOriginal;
          this.mostrarGerarPdf = true;
          this.mostrarVoltar = true;
        }, 300);
      });
    }
  }
};
</script>

<style scoped>
.relatorio-status-parceiro {
  min-height: 100vh;
  background: #f4f4f4;
}

.row-top {
  position: relative;
}

.col-top {
  flex: 0 0 120px;
  min-width: 120px;
}

.col-top-left {
  text-align: left;
}

.col-top-right {
  text-align: right;
}

.titulo-relatorio {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  text-align: center;
}

.print-only {
  display: none;
}

.print-only.espaco-ano {
  margin-left: 0.35em;
}

.logo-joule .img-logo {
  height: 48px;
  width: auto;
}

.relatorio-status-parceiro .table tbody {
  font-size: 1.05rem;
}

.relatorio-status-parceiro .table {
  border-collapse: collapse !important;
  border: none !important;
}

.relatorio-status-parceiro .table th,
.relatorio-status-parceiro .table td {
  border: 1px solid #dee2e6 !important;
  border-right-width: 1px !important;
  border-left-width: 1px !important;
}

.relatorio-card .card-header {
  background-color: transparent;
  border-bottom: none;
  padding-bottom: 0.5rem;
}

.relatorio-card .card-body {
  padding-top: 0.5rem;
}

.relatorio-status-parceiro .tabela-status th,
.relatorio-status-parceiro .tabela-status td,
.relatorio-status-parceiro .tabela-resumo th,
.relatorio-status-parceiro .tabela-resumo td {
  white-space: nowrap;
}

.relatorio-status-parceiro .tabela-status,
.relatorio-status-parceiro .tabela-resumo {
  table-layout: fixed;
  width: 100%;
}

.relatorio-status-parceiro .tabela-status th:first-child,
.relatorio-status-parceiro .tabela-status td:first-child,
.relatorio-status-parceiro .tabela-resumo th:first-child,
.relatorio-status-parceiro .tabela-resumo td:first-child {
  width: 10%;
}

.relatorio-status-parceiro .tabela-status th:not(:first-child),
.relatorio-status-parceiro .tabela-status td:not(:first-child),
.relatorio-status-parceiro .tabela-resumo th:not(:first-child),
.relatorio-status-parceiro .tabela-resumo td:not(:first-child) {
  width: auto;
  text-align: center !important;
}

.relatorio-card {
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
}

@media screen and (max-width: 767px) {
  .relatorio-status-parceiro .row-top {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
  .relatorio-status-parceiro .row-top .col-top {
    flex: none;
    min-width: 0;
    text-align: center;
  }
  .relatorio-status-parceiro .row-top .titulo-relatorio {
    position: static;
    transform: none;
    order: 0;
  }
  .relatorio-status-parceiro .row-top .col-top-left {
    order: -1;
  }
  .relatorio-status-parceiro .row-top .col-top-right {
    order: 1;
  }
  .relatorio-status-parceiro .relatorio-card .card-body {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .relatorio-status-parceiro .tabela-status,
  .relatorio-status-parceiro .tabela-resumo {
    min-width: 600px;
  }
  .relatorio-status-parceiro .tabela-status th:first-child,
  .relatorio-status-parceiro .tabela-status td:first-child,
  .relatorio-status-parceiro .tabela-resumo th:first-child,
  .relatorio-status-parceiro .tabela-resumo td:first-child {
    width: 15%;
  }
}

@media print {
  @page {
    margin: 12mm;
  }

  .print-only {
    display: inline !important;
  }
  .logo-joule.print-only {
    display: block !important;
  }

  .relatorio-status-parceiro {
    background: white;
  }
  .no-print {
    display: none !important;
  }
  .relatorio-card {
    break-inside: avoid;
  }
  .relatorio-status-parceiro .table {
    border-collapse: collapse !important;
    border: none !important;
  }
  .relatorio-status-parceiro .table th,
  .relatorio-status-parceiro .table td {
    border: 1px solid #000 !important;
    border-right-width: 1px !important;
    border-left-width: 1px !important;
  }
  .relatorio-status-parceiro .tabela-status th.col-aguardando-inspecao,
  .relatorio-status-parceiro .tabela-status td.col-aguardando-inspecao,
  .relatorio-status-parceiro .tabela-resumo th.col-aguardando-inspecao,
  .relatorio-status-parceiro .tabela-resumo td.col-aguardando-inspecao {
    width: 13%;
  }
}
</style>
